'use strict'
/**
 * refrescoQueNoInsiste.js · EL REFRESCO DE SESIÓN TIENE UNA N, Y LA ESPERA NO SE REINICIA.
 * ════════════════════════════════════════════════════════════════════════════════════════
 * Orden del founder (2026-10-07): «**GRAVE · bucle de refresco**… Espera creciente que **no se reinicia**; tras **N fallos** borra sesión y va a la
 * pantalla de entrada; el SW no reintenta `/auth/v1/token` en 5xx. Medida en la reproducción: **19,6/min → ≤1 cada 30 s y parada en N**».
 *
 * 🔴 LA FILA, medida en los logs del borde de Supabase el 7-oct entre las **13:15Z y las 15:10Z**: **2.000 peticiones y ni una con éxito**, de las que
 * **1.940 (97 %)** eran `POST /auth/v1/token?grant_type=refresh_token` devolviendo **504**, todas de **un solo** iPhone, a **17 por minuto durante 1 h 50**.
 * Mientras duró, la base no respondía a nadie: las 7 lecturas de `day_plans` de los seis arranques de esa ventana murieron en la página 1 y **ningún día
 * roto se regeneró**. Supabase no declaraba incidente y el proyecto estaba `ACTIVE_HEALTHY`: la carga era nuestra, y era este bucle.
 *
 * EL MECANISMO, leído del bundle servido (`auth-js` 2.105.3, `_refreshAccessToken`):
 *
 *     retryable(
 *       async (n) => { if (n > 0) await dormir(200 * 2 ** (n - 1)); return await pedirToken() },
 *       (n, err) => err && esReintentable(err) && Date.now() + 200 * 2 ** n - empezoEn < 30_000,
 *     )                                         // y dentro: for (let i = 0; i < Infinity; i++)
 *
 * O sea: la espera creciente **ya existe** —dobla desde 200 ms— y el bucle se corta cuando lo siguiente no cabría en **30 s**. Pero el temporizador de
 * refresco vuelve a disparar **a los 30 s** y arranca otra tanda desde el principio. Medido en la reproducción, los huecos entre peticiones:
 *
 *     0,1 · 0,2 · 0,4 · 0,8 · 1,6 · 3,2 · 6,4 · 12,8   →   0,1 · 0,2 · 0,4 · 0,8 · …
 *
 * **Lo que falta no es la espera: es la N.** El contador se reinicia con cada tanda y nadie se rinde nunca. Y en este camino la sesión **no se borra**, así
 * que siempre hay algo que reintentar — con el service worker bloqueado la app hace **un** intento, borra la sesión y aparece la pantalla de entrada, que
 * es el comportamiento correcto; con el SW permitido, 19,6 por minuto para siempre.
 *
 * QUÉ HACE ESTO. No parchea la librería: le pone un `fetch` propio delante (`global.fetch` del cliente) y desde ahí
 *   1. **cuenta** los fallos 5xx consecutivos del endpoint de token y mantiene la cuenta **entre tandas** (ahí está la N que faltaba);
 *   2. **corta la tanda** devolviendo un error que `auth-js` NO considera reintentable, para que su propio `retryable` pare en vez de doblar ocho veces;
 *   3. **espacia**: mientras el fallo dure, deja pasar como mucho **un** intento cada 30 s, y el resto los responde sin tocar la red;
 *   4. al llegar a **N**, **borra la sesión** y avisa, que es lo que lleva a la pantalla de entrada.
 *
 * Pieza · quién la alimenta · quién la dispara:
 *   `fetchDeAuthQueNoInsiste` · el `fetch` del cliente de Supabase (`src/lib/supabase.js`) · un teléfono con la sesión caducada y la base sin responder.
 */

/** Lo que el founder fijó: como mucho un intento cada 30 s mientras el fallo dure. Es el mismo tic que usa `auth-js`. */
const MINIMO_ENTRE_INTENTOS_MS = 30_000
/** La N: tras tantos fallos seguidos, se deja de insistir y se va a la pantalla de entrada. */
const FALLOS_ANTES_DE_RENDIRSE = 5
/** Un 5xx del borde: el origen no contestó. Un 400 («invalid_grant») no es esto — ése ya lo resuelve la librería borrando la sesión. */
const esDelBorde = (status) => status >= 500 && status <= 599

const ES_TOKEN = /\/auth\/v1\/token(\?|$)/

/**
 * Envuelve un `fetch` para que el refresco de sesión no insista.
 *
 * @param {Function} fetchBase · el `fetch` de verdad
 * @param {{alRendirse?: Function, ahora?: Function, minimoMs?: number, n?: number}} opciones
 *        `alRendirse` se llama UNA vez al llegar a la N (ahí se borra la sesión y se manda a la entrada).
 * @returns {Function} un `fetch` con la misma firma
 */
function fetchDeAuthQueNoInsiste(fetchBase, { alRendirse = null, ahora = () => Date.now(), minimoMs = MINIMO_ENTRE_INTENTOS_MS, n = FALLOS_ANTES_DE_RENDIRSE } = {}) {
  // el estado vive en la clausura: una cuenta por cliente, que es lo que hace que la N sobreviva a las tandas
  let fallos = 0
  let ultimoIntento = 0
  let rendido = false

  return async function fetchQueNoInsiste(entrada, init) {
    const url = String((entrada && entrada.url) || entrada || '')
    if (!ES_TOKEN.test(url)) return fetchBase(entrada, init)

    // ya nos rendimos: no se toca la red más. Devolver un 503 propio y NO reintentable deja a la librería terminar limpio.
    if (rendido) return respuestaQueNoSeReintenta('la sesión no se pudo refrescar y ya se dejó de intentar')

    const desdeElUltimo = ahora() - ultimoIntento
    // 3 · espaciado: mientras el fallo dure, un intento cada 30 s. Lo de más no sale a la red — ahí estaban las 1.940 peticiones.
    if (fallos > 0 && desdeElUltimo < minimoMs) {
      return respuestaQueNoSeReintenta(`el refresco anterior falló hace ${Math.round(desdeElUltimo / 1000)} s; se espera a los ${Math.round(minimoMs / 1000)} s`)
    }

    ultimoIntento = ahora()
    let r
    try {
      r = await fetchBase(entrada, init)
    } catch (e) {
      // un fallo de red cuenta igual que un 5xx: desde fuera es lo mismo, «no hay respuesta»
      fallos += 1
      if (fallos >= n) rendirse()
      throw e
    }
    if (r && esDelBorde(r.status)) {
      fallos += 1
      // 2 · se corta la tanda: con un cuerpo que `auth-js` no toma por reintentable, su `retryable` para en vez de doblar ocho veces
      if (fallos >= n) rendirse()
      return respuestaQueNoSeReintenta(`el borde devolvió ${r.status} al refrescar la sesión (fallo ${fallos} de ${n})`, r.status)
    }
    if (r && r.ok) { fallos = 0; rendido = false }
    return r
  }

  function rendirse() {
    if (rendido) return
    rendido = true
    try { if (alRendirse) alRendirse({ fallos, n }) } catch (e) { /* rendirse no puede fallar: es el camino de salida */ }
  }
}

/**
 * Una respuesta que la librería NO reintenta.
 *
 * `auth-js` reintenta cuando el error es «de red» (`AuthRetryableFetchError`), y eso lo decide por el **status**: un 4xx no lo es. Así que se devuelve un
 * **400** con el cuerpo de error que la librería entiende, y el motivo de verdad va dentro para que se pueda leer en el parte o en la consola. Es la
 * diferencia entre «no insistas» y «vuelve a intentarlo ocho veces».
 */
function respuestaQueNoSeReintenta(porque, statusReal = null) {
  const cuerpo = JSON.stringify({ error: 'refresco_pospuesto', error_description: porque, ...(statusReal ? { status_del_borde: statusReal } : {}) })
  return new Response(cuerpo, { status: 400, headers: { 'Content-Type': 'application/json' } })
}

module.exports = { fetchDeAuthQueNoInsiste, MINIMO_ENTRE_INTENTOS_MS, FALLOS_ANTES_DE_RENDIRSE, ES_TOKEN, esDelBorde }
